import service from "@/store/services/mail-v2-service";

export const state = () => ({
  // Types + templates merged list
  templates: [],
  currentTemplate: null,

  // Admin global list
  allTypes: [],
  allTypesMeta: {},

  // Types only
  types: [],
  currentType: null,

  // Settings
  settings: {},

  // Logs
  logs: [],
  logsMeta: {},

  // Preview
  previewHtml: "",

  loading: false,
});

export const mutations = {
  SET_TEMPLATES(state, templates) {
    state.templates = templates;
  },
  SET_ALL_TYPES(state, types) {
    state.allTypes = types;
  },
  SET_ALL_TYPES_META(state, meta) {
    state.allTypesMeta = meta;
  },
  SET_CURRENT_TEMPLATE(state, template) {
    state.currentTemplate = template;
  },
  SET_TYPES(state, types) {
    state.types = types;
  },
  SET_CURRENT_TYPE(state, type) {
    state.currentType = type;
  },
  SET_SETTINGS(state, settings) {
    state.settings = settings;
  },
  SET_LOGS(state, logs) {
    state.logs = logs;
  },
  SET_LOGS_META(state, meta) {
    state.logsMeta = meta;
  },
  SET_PREVIEW_HTML(state, html) {
    state.previewHtml = html;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  RESET(state) {
    state.currentTemplate = null;
    state.currentType = null;
    state.previewHtml = "";
  },
};

export const actions = {
  /* =========================================================
   *                        TYPES
   * ========================================================= */
  async createMailType({ commit }, { data }) {
    commit("SET_LOADING", true);
    try {
      return await service.createMailType(data);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async sendTestAdmin(_, { typeId, data }) {
    return service.sendTestAdmin(typeId, data);
  },

  // Layouts
  async listLayouts({ commit }, { params = {} } = {}) {
    commit("SET_LOADING", true);
    try {
      return await service.listLayouts(params);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async createLayout({ commit }, { data }) {
    commit("SET_LOADING", true);
    try {
      return await service.createLayout(data);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async getLayout(_, { layoutId }) {
    return service.getLayout(layoutId);
  },

  async updateLayout({ commit }, { layoutId, data }) {
    commit("SET_LOADING", true);
    try {
      return await service.updateLayout(layoutId, data);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async previewLayout(_, { layoutId, data }) {
    return service.previewLayout(layoutId, data);
  },

  async updateMailType({ commit }, { typeId, data }) {
    commit("SET_LOADING", true);
    try {
      return await service.updateMailType(typeId, data);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async getMailType({ commit }, { typeId }) {
    commit("SET_LOADING", true);
    try {
      return await service.getMailType(typeId);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async updateDefaultTemplate({ commit }, { typeId, data }) {
    commit("SET_LOADING", true);
    try {
      return await service.updateDefaultTemplate(typeId, data);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async listAllTypes({ commit }, { params = {} }) {
    commit("SET_LOADING", true);
    try {
      const result = await service.listAllTypes(params);
      commit("SET_ALL_TYPES", result.data);
      commit("SET_ALL_TYPES_META", result.meta);
      return result;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async listTypes({ commit }, { orgId, params = {} }) {
    commit("SET_LOADING", true);
    try {
      const types = await service.listTypes(orgId, params);
      commit("SET_TYPES", types);
      return types;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async getType({ commit }, { orgId, typeId }) {
    commit("SET_LOADING", true);
    try {
      const type = await service.getType(orgId, typeId);
      commit("SET_CURRENT_TYPE", type);
      return type;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /* =========================================================
   *                      TEMPLATES
   * ========================================================= */
  async listTemplates({ commit }, { orgId, params = {} }) {
    commit("SET_LOADING", true);
    try {
      const templates = await service.listTemplates(orgId, params);
      commit("SET_TEMPLATES", templates);
      return templates;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async customizeTemplate({ commit }, { orgId, mailTypeId }) {
    commit("SET_LOADING", true);
    try {
      const template = await service.customizeTemplate(orgId, mailTypeId);
      commit("SET_CURRENT_TEMPLATE", template);
      return template;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async updateTemplate({ commit }, { orgId, templateId, data }) {
    commit("SET_LOADING", true);
    try {
      const template = await service.updateTemplate(orgId, templateId, data);
      commit("SET_CURRENT_TEMPLATE", template);
      return template;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async deleteTemplate(_, { orgId, templateId }) {
    return service.deleteTemplate(orgId, templateId);
  },

  async previewTemplate({ commit }, { orgId, data }) {
    try {
      const result = await service.previewTemplate(orgId, data);
      commit("SET_PREVIEW_HTML", result.html || "");
      return result;
    } catch {
      return { html: "" };
    }
  },

  async previewRaw({ commit }, { orgId, data }) {
    try {
      const result = await service.previewRaw(orgId, data);
      commit("SET_PREVIEW_HTML", result.html || "");
      return result;
    } catch {
      return { html: "" };
    }
  },

  async sendTest(_, { orgId, data }) {
    return service.sendTest(orgId, data);
  },

  /* =========================================================
   *                      SETTINGS
   * ========================================================= */
  async getSettings({ commit }, { orgId }) {
    commit("SET_LOADING", true);
    try {
      const settings = await service.getSettings(orgId);
      commit("SET_SETTINGS", settings);
      return settings;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async updateSettings({ commit }, { orgId, data }) {
    commit("SET_LOADING", true);
    try {
      const settings = await service.updateSettings(orgId, data);
      commit("SET_SETTINGS", settings);
      return settings;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /* =========================================================
   *                        LOGS
   * ========================================================= */
  async listLogs({ commit }, { orgId, params = {} }) {
    commit("SET_LOADING", true);
    try {
      const result = await service.listLogs(orgId, params);
      commit("SET_LOGS", result.data);
      commit("SET_LOGS_META", result.meta);
      return result;
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

export const getters = {
  allTypes: (state) => state.allTypes,
  allTypesMeta: (state) => state.allTypesMeta,
  templates: (state) => state.templates,
  currentTemplate: (state) => state.currentTemplate,
  types: (state) => state.types,
  currentType: (state) => state.currentType,
  settings: (state) => state.settings,
  logs: (state) => state.logs,
  logsMeta: (state) => state.logsMeta,
  previewHtml: (state) => state.previewHtml,
  loading: (state) => state.loading,

  // Templates qui ont été personnalisés par l'org
  customizedTemplates: (state) =>
    (state.templates || []).filter((t) => t.is_customized),

  // Templates qui utilisent encore le default
  defaultTemplates: (state) =>
    (state.templates || []).filter((t) => !t.is_customized),

  // Types dropdown
  dropdownTypes: (state) =>
    (state.types || []).map((t) => ({
      id: t.id,
      name: t.name?.fr || t.name?.en || t.code,
      code: t.code,
    })),
};

const mailV2 = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default mailV2;
